import React from 'react';
import { Edit2, Trash2, HelpCircle, Shield, Palette } from 'lucide-react';
import type { Position } from '../../types/depth-chart';
import type { Player } from '../../types/index';

interface DepthChartEntry {
  id?: string;
  team_id: string;
  player_id: string | null;
  position: string;
  depth: number;
  unit: string;
  // No special_teams_unit needed - formation field handles Special Teams
  color?: string;
}

interface DepthChartTableProps {
  positions: Position[];
  depthChart: DepthChartEntry[];
  players: Player[];
  activeTab: string;
  activeDepth: number;
  depthStrings: { id: number; name: string; active: boolean }[];
  editingPosition: string | null;
  editedPositionName: string;
  handlePositionEdit: (position: Position) => void;
  handlePlayerChange: (position: string, depth: number, playerId: string | null, inning?: number) => void;
  deleteCustomPosition: (position: Position) => void;
  showTooltip: string | null;
  setShowTooltip: (position: string | null) => void;
  savePositionEdit: () => void;
  cancelPositionEdit: () => void;
  coloringPosition: { position: string, depth: number } | null;
  setColoringPosition: (pos: { position: string, depth: number } | null) => void;
  handlePositionColorChange: (positionKey: string, depth: number, color: string, inning?: number) => void;
  setEditedPositionName: (name: string) => void;
  currentInning?: number;
  isBaseballFielding?: boolean;
  playerSortBy: 'firstName' | 'lastName' | 'number';
  editedPositionFullName?: string;
  setEditedPositionFullName?: (name: string) => void;
  disablePositionEditing?: boolean;
}

const DepthChartTable: React.FC<DepthChartTableProps> = ({
  positions,
  depthChart,
  players,
  activeTab,
  activeDepth,
  depthStrings,
  editingPosition,
  editedPositionName,
  handlePositionEdit,
  handlePlayerChange,
  deleteCustomPosition,
  showTooltip,
  setShowTooltip,
  savePositionEdit,
  cancelPositionEdit,
  coloringPosition,
  setColoringPosition,
  handlePositionColorChange,
  setEditedPositionName,
  currentInning = 1,
  isBaseballFielding = false,
  playerSortBy,
  editedPositionFullName = '',
  setEditedPositionFullName = () => {},
  disablePositionEditing = false
}) => {
  const getPositionColor = (position: string, depth: number): string => {
    // For baseball fielding, use currentInning as the depth to search
    const actualDepth = isBaseballFielding ? currentInning : depth;
    const entry = depthChart.find(
      e => e.position === position && e.depth === actualDepth && e.unit === activeTab
    );
    return entry?.color || '';
  };
  
  const getSelectedPlayerId = (position: string, depth: number): string => {
    // For baseball fielding, use currentInning as the depth to search
    const actualDepth = isBaseballFielding ? currentInning : depth;
    const entry = depthChart.find(
      e => e.position === position && e.depth === actualDepth && e.unit === activeTab
    );
    return entry?.player_id || '';
  };

  const getSortedPlayers = () => {
    return [...players].sort((a, b) => {
      switch (playerSortBy) {
        case 'firstName':
          return a.first_name.localeCompare(b.first_name);
        case 'lastName':
          return a.last_name.localeCompare(b.last_name);
        case 'number':
        default:
          return (a.number || 0) - (b.number || 0);
      }
    });
  };

  const formatPlayerDisplayName = (player: Player): string => {
    const base = `${player.first_name} ${player.last_name} (#${player.number ?? '—'})`;
    const injuryLabel = player.injury_status && player.injury_status !== 'H' ? ` (${player.injury_status})` : '';
    return base + injuryLabel;
  };

  const getSelectedPlayer = (position: string, depth: number): Player | null => {
    const playerId = getSelectedPlayerId(position, depth);
    return players.find(p => p.id === playerId) || null;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-stone bg-opacity-10">
            <th className="py-3 px-4 text-left text-black font-medium w-40">Pos</th>
            {activeTab !== 'Special Teams' ? (
              <>
                {depthStrings.filter(ds => ds.active).map(depthString => (
                  <th key={depthString.id} className="py-3 px-4 text-left text-black font-medium">
                    {depthString.name}
                  </th>
                ))}
              </>
            ) : (
              <th className="py-3 px-4 text-left text-black font-medium">Player</th>
            )}
          </tr>
        </thead>
        <tbody>
          {positions.length > 0 ? positions.map(position => (
            <tr key={position.key} className="border-b border-stone border-opacity-10">
              <td className="py-3 px-4 relative group">
                <div className="flex items-center">
                  <div className="relative">
                    <span
                      className="font-medium px-2 py-1 rounded"
                      style={{ backgroundColor: position.color ? position.color : 'transparent' }}
                    >
                      {editingPosition === position.key ? (
                        <div className="space-y-1">
                          <input
                            type="text"
                            value={editedPositionName}
                            onChange={(e) => setEditedPositionName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') savePositionEdit();
                              if (e.key === 'Escape') cancelPositionEdit();
                            }}
                            className="w-full p-1 border border-stone border-opacity-20 rounded text-sm"
                            placeholder="Position abbreviation"
                            autoFocus
                          />
                          <input
                            type="text"
                            value={editedPositionFullName}
                            onChange={(e) => setEditedPositionFullName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') savePositionEdit();
                              if (e.key === 'Escape') cancelPositionEdit();
                            }}
                            className="w-full p-1 border border-stone border-opacity-20 rounded text-sm text-xs"
                            placeholder="Full position name"
                          />
                          <div className="flex gap-1">
                            <button
                              onClick={savePositionEdit}
                              className="px-2 py-0.5 text-xs bg-primary text-white rounded hover:bg-primary-dark"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelPositionEdit}
                              className="px-2 py-0.5 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        position.name
                      )}
                    </span>
                    <button
                      className="ml-1 text-stone inline-flex"
                      onMouseEnter={() => setShowTooltip(position.key)}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      <HelpCircle className="w-4 h-4" />
                    </button>
                    {showTooltip === position.key && (
                      <div className="absolute left-0 top-full mt-1 bg-dark text-white text-xs rounded px-2 py-1 z-20 whitespace-nowrap">
                        {position.fullName}
                      </div>
                    )}
                  </div>
                  {position.custom && (
                    <button
                      onClick={() => deleteCustomPosition(position)}
                      className="ml-2 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete custom position"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  {!disablePositionEditing && (
                    <button
                      onClick={() => {
                        handlePositionEdit(position);
                      }}
                      className={`ml-2 opacity-0 group-hover:opacity-100 transition-opacity ${
                        position.custom ? 'text-primary' : 'text-stone'
                      }`}
                      title={position.custom ? "Edit custom position label" : "Create custom position label"}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  {position.custom && (
                    <span className="ml-1 text-xs text-primary opacity-75" title="Custom label">
                      ●
                    </span>
                  )}
                </div>
              </td>
              {activeTab !== 'Special Teams' ? (
                // Offense and Defense: show depth slots for active depth strings
                <>
                  {depthStrings.filter(ds => ds.active).map(depthString => (
                    <td key={depthString.id} className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1 min-w-0 w-20 max-w-20">
                          <select
                            value={getSelectedPlayerId(position.key, isBaseballFielding ? currentInning : depthString.id)}
                            onChange={(e) => handlePlayerChange(position.key, isBaseballFielding ? currentInning : depthString.id, e.target.value || null, isBaseballFielding ? currentInning : undefined)}
                            className="w-full p-2 border border-stone border-opacity-20 rounded text-black"
                          >
                            <option value="">— Not Set —</option>
                            {getSortedPlayers().map(player => (
                              <option key={player.id} value={player.id}>
                                {formatPlayerDisplayName(player)}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Color palette button next to dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setColoringPosition({
                              position: position.key,
                              depth: isBaseballFielding ? currentInning : depthString.id
                            })}
                            className="p-1 rounded hover:bg-gray-100"
                            title="Change position color"
                          >
                            <Palette 
                              size={16} 
                              className="text-gray-600 hover:text-gray-800"
                              style={{ 
                                color: getPositionColor(position.key, isBaseballFielding ? currentInning : depthString.id) || '#6b7280'
                              }}
                            />
                          </button>
                          
                          {/* Color palette popup */}
                          {coloringPosition && coloringPosition.position === position.key && coloringPosition.depth === (isBaseballFielding ? currentInning : depthString.id) && (
                            <div className="absolute right-0 top-full mt-1 bg-white border border-stone border-opacity-20 rounded-lg p-3 shadow-lg" style={{ zIndex: 9999 }}>
                              <div className="mb-2">
                                <label className="block text-xs font-medium mb-1">Position Color</label>
                                <div className="flex gap-1 flex-wrap">
                                  {['', '#ff0000', '#ff6b00', '#ffcc00', '#7bcc2c', '#00b341', '#00ccc9', '#0066ff', '#6e3bef', '#cc00ff', '#ff0099'].map(color => (
                                    <button
                                      key={color}
                                      type="button"
                                      className={`w-6 h-6 rounded-full border ${
                                        color === getPositionColor(position.key, isBaseballFielding ? currentInning : depthString.id) ? 'ring-2 ring-offset-2 ring-primary' : 'border-stone'
                                      }`}
                                      style={{ backgroundColor: color || 'transparent' }}
                                      onClick={() => handlePositionColorChange(
                                        position.key, 
                                        isBaseballFielding ? currentInning : depthString.id, 
                                        color,
                                        isBaseballFielding ? currentInning : undefined
                                      )}
                                    />
                                  ))}
                                </div>
                              </div>
                              
                              <div className="flex justify-end">
                                <button
                                  onClick={() => setColoringPosition(null)}
                                  className="px-2 py-1 text-xs border border-stone border-opacity-20 rounded-md text-dark"
                                >
                                  Close
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  ))}
                </>
              ) : (
                // Special Teams: only show one player slot
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1 min-w-0 w-20 max-w-20">
                      <select
                        value={getSelectedPlayerId(position.key, 1)}
                        onChange={(e) => handlePlayerChange(position.key, 1, e.target.value || null)}
                        className="w-full p-2 border border-stone border-opacity-20 rounded text-black"
                      >
                        <option value="">— Not Set —</option>
                        {getSortedPlayers().map(player => (
                          <option key={player.id} value={player.id}>
                            {formatPlayerDisplayName(player)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Color palette button next to dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setColoringPosition({
                          position: position.key,
                          depth: 1
                        })}
                        className="p-1 rounded hover:bg-gray-100"
                        title="Change position color"
                      >
                        <Palette 
                          size={16} 
                          className="text-gray-600 hover:text-gray-800"
                          style={{ 
                            color: getPositionColor(position.key, 1) || '#6b7280'
                          }}
                        />
                      </button>
                      
                      {/* Color palette popup */}
                      {coloringPosition && coloringPosition.position === position.key && coloringPosition.depth === 1 && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-stone border-opacity-20 rounded-lg p-3 shadow-lg" style={{ zIndex: 9999 }}>
                          <div className="mb-2">
                            <label className="block text-xs font-medium mb-1">Position Color</label>
                            <div className="flex gap-1 flex-wrap">
                              {['', '#ff0000', '#ff6b00', '#ffcc00', '#7bcc2c', '#00b341', '#00ccc9', '#0066ff', '#6e3bef', '#cc00ff', '#ff0099'].map(color => (
                                <button
                                  key={color}
                                  type="button"
                                  className={`w-6 h-6 rounded-full border ${color === getPositionColor(position.key, 1) ? 'ring-2 ring-offset-2 ring-primary' : 'border-stone'}`}
                                  style={{ backgroundColor: color || 'transparent' }}
                                  onClick={() => handlePositionColorChange(position.key, 1, color)}
                                />
                              ))}
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <button
                              onClick={() => setColoringPosition(null)}
                              className="px-2 py-1 text-xs border border-stone border-opacity-20 rounded-md text-dark"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              )}
            </tr>
          )) : null}
        </tbody>
      </table>
    </div>
  );
};

export default DepthChartTable; 